import { infiniteQueryOptions, queryOptions, useMutation } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import type { NaicsResponse } from '../industryForm/industryForm.types';
import type { IndustryItem } from '../../industryOld/industryForm/industryForm.types';

interface GetIndustryListParams {
  customerId: string;
  isEmbedded: boolean;
}
// This is only used for non-profit businesses
const getIndustryList = async ({ customerId, isEmbedded }: GetIndustryListParams) => {
  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/industryList`
    : `v3/secureonboarding/user/${customerId}/industryList`;
  const params = new URLSearchParams();
  params.set('industryType', 'NON_PROFIT');
  const { data } = await HttpService.get<{ industryList: IndustryItem[] }>(url, {
    params,
  });
  return data.industryList;
};

export const getIndustryListQueryOptions = ({ customerId, isEmbedded }: GetIndustryListParams) =>
  queryOptions({
    queryKey: ['industryList', customerId, isEmbedded] as const,
    queryFn: ({ queryKey: [_, queryKeyCustomerId, queryKeyIsEmbedded] }) =>
      getIndustryList({ customerId: queryKeyCustomerId, isEmbedded: queryKeyIsEmbedded }),
  });

interface GetNaicsListParams {
  searchText: string;
  page: number;
  pageSize: number;
  customerId: string;
  isEmbedded: boolean;
}
const getNaicsList = async ({
  searchText,
  page,
  pageSize,
  customerId,
  isEmbedded,
}: GetNaicsListParams) => {
  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('pageSize', pageSize.toString());
  params.set('searchText', searchText);

  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/naicsCode/search`
    : `v3/secureonboarding/user/${customerId}/naicsCode/search`;

  const { data } = await HttpService.get<NaicsResponse>(url, { params });
  return data;
};

const PAGE_SIZE = 50;

interface GetNaicsListInfiniteQueryParams {
  customerId: string;
  isEmbedded: boolean;
  searchText: string;
}
export const getNaicsListInfiniteQueryOptions = ({
  customerId,
  isEmbedded,
  searchText,
}: GetNaicsListInfiniteQueryParams) =>
  infiniteQueryOptions({
    queryKey: ['naicsList', customerId, isEmbedded, searchText] as const,
    queryFn: ({ pageParam }) =>
      getNaicsList({ searchText, page: pageParam, pageSize: PAGE_SIZE, customerId, isEmbedded }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.number + 2),
    staleTime: Infinity,
  });

interface UpdateIndustryDataParams {
  naicsCode: string;
  naicsCodeTitle?: string;
  customerId: string;
  isEmbedded: boolean;
}
const updateIndustryDataApi = async ({
  naicsCode,
  naicsCodeTitle,
  customerId,
  isEmbedded,
}: UpdateIndustryDataParams) => {
  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/naicsCode`
    : `v3/secureonboarding/user/${customerId}/naicsCode`;

  const { data } = await HttpService.post(url, { naicsCode, naicsCodeTitle });
  return data;
};

interface UseUpdateIndustryDataParams {
  customerId: string;
  isEmbedded: boolean;
}
type UpdateIndustryDataMutationParams = Omit<UpdateIndustryDataParams, 'customerId' | 'isEmbedded'>;
export const useUpdateIndustryData = ({ customerId, isEmbedded }: UseUpdateIndustryDataParams) =>
  useMutation({
    mutationKey: ['updateIndustryData', customerId, isEmbedded] as const,
    mutationFn: ({ naicsCode, naicsCodeTitle }: UpdateIndustryDataMutationParams) =>
      updateIndustryDataApi({ naicsCode, naicsCodeTitle, customerId, isEmbedded }),
  });

interface UpdateOldIndustryDataParams {
  naicsIndustry: string;
  customerId: string;
  isEmbedded: boolean;
}
export const updateOldIndustryDataApi = async ({
  naicsIndustry,
  customerId,
  isEmbedded,
}: UpdateOldIndustryDataParams) => {
  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/naicsSubIndustry`
    : `v3/secureonboarding/user/${customerId}/naicsSubIndustry`;

  const { data } = await HttpService.post(url, {
    naicsSubIndustry: naicsIndustry,
  });
  return data;
};
interface UseUpdateOldIndustryDataParams {
  customerId: string;
  isEmbedded: boolean;
}
type UpdateOldIndustryDataMutationParams = Omit<
  UpdateOldIndustryDataParams,
  'customerId' | 'isEmbedded'
>;
export const useUpdateOldIndustryData = ({
  customerId,
  isEmbedded,
}: UseUpdateOldIndustryDataParams) =>
  useMutation({
    mutationKey: ['updateOldIndustryData', customerId, isEmbedded] as const,
    mutationFn: ({ naicsIndustry }: UpdateOldIndustryDataMutationParams) =>
      updateOldIndustryDataApi({ naicsIndustry, customerId, isEmbedded }),
  });
